import { Button, Label, NumberInput } from '@/components'
import { PATCH_COMPANY_TACHOGRAPH_INFRINGEMENT_SETTINGS } from '@/modules/tachograph-infringements/mutations'
import { COMPANY_TACHOGRAPH_INFRINGEMENT_SETTINGS_QUERY } from '@/modules/tachograph-infringements/queries'
import { useOnErrorAuto } from '@/utils/hooks'
import { useMutation, useQuery } from '@apollo/client'
import { useTranslate } from '@ur-solutions/react-hooks'
import type { ResultOf } from 'gql.tada'
import React, { useState } from 'react'
import { InfoBox, Section, SettingsWrapper } from '../components/common'
import { SettingRow } from '../components/setting-row'

type SettingsQuery = ResultOf<
  typeof COMPANY_TACHOGRAPH_INFRINGEMENT_SETTINGS_QUERY
>
type Settings = SettingsQuery['companyTachographInfringementSettings']

type RuleSettingField =
  | 'countryStartEnabled'
  | 'countryStopEnabled'
  | 'manualEntryEnabled'
  | 'manualEntryVehicleChangeEnabled'
  | 'borderCrossingEnabled'

type SettingsForm = Pick<
  Settings,
  | 'generationEnabled'
  | RuleSettingField
  | 'minManualEntryGapMinutes'
  | 'maxManualEntryGapMinutes'
>

const RULE_SETTING_FIELDS: Record<string, RuleSettingField> = {
  T1: 'countryStartEnabled',
  T2: 'countryStopEnabled',
  T3: 'manualEntryEnabled',
  T4: 'manualEntryVehicleChangeEnabled',
  T5: 'borderCrossingEnabled',
}

export const TachographInfringementSettings: React.FC = () => {
  const { data, loading } = useQuery(
    COMPANY_TACHOGRAPH_INFRINGEMENT_SETTINGS_QUERY
  )
  if (loading || !data) return null
  return (
    <TachographInfringementSettingsForm
      settings={data.companyTachographInfringementSettings}
      categories={data.tachographInfringementCategories}
    />
  )
}

const TachographInfringementSettingsForm: React.FC<{
  settings: Settings
  categories: SettingsQuery['tachographInfringementCategories']
}> = ({ settings, categories }) => {
  const t = useTranslate({
    title: 'infringements.tachograph-infringements',
    info: 'settings.tachograph-infringements.info',
    generation: 'settings.tachograph-infringements.generation',
    generationInfo: 'settings.tachograph-infringements.generation-info',
    rules: 'settings.tachograph-infringements.rules',
    minGap: 'settings.tachograph-infringements.min-gap',
    maxGap: 'settings.tachograph-infringements.max-gap',
    gapInfo: 'settings.tachograph-infringements.gap-info',
    minutes: 'common.minutes',
    save: 'common.save',
  })
  const onErrorAuto = useOnErrorAuto()
  const [form, setForm] = useState<SettingsForm>({
    generationEnabled: settings.generationEnabled,
    countryStartEnabled: settings.countryStartEnabled,
    countryStopEnabled: settings.countryStopEnabled,
    manualEntryEnabled: settings.manualEntryEnabled,
    manualEntryVehicleChangeEnabled: settings.manualEntryVehicleChangeEnabled,
    borderCrossingEnabled: settings.borderCrossingEnabled,
    minManualEntryGapMinutes: settings.minManualEntryGapMinutes,
    maxManualEntryGapMinutes: settings.maxManualEntryGapMinutes,
  })
  const [patchSettings, { loading: saving }] = useMutation(
    PATCH_COMPANY_TACHOGRAPH_INFRINGEMENT_SETTINGS,
    { onError: onErrorAuto() }
  )

  const update = (patch: Partial<SettingsForm>) =>
    setForm(current => ({ ...current, ...patch }))

  return (
    <SettingsWrapper grid={{ flow: 'row' }}>
      <InfoBox>
        <p>{t.info}</p>
      </InfoBox>
      <Section header={t.title}>
        <form
          className="space-y-8"
          onSubmit={event => {
            event.preventDefault()
            void patchSettings({ variables: form })
          }}
        >
          <SettingRow
            id="tachograph-generation"
            label={t.generation}
            description={t.generationInfo}
            checked={form.generationEnabled}
            onCheckedChange={checked => update({ generationEnabled: checked })}
          />

          <fieldset className="space-y-2">
            <legend className="mb-2 text-xs font-semibold uppercase tracking-[0.22em] text-stone-500">
              {t.rules}
            </legend>
            <div className="divide-y divide-stone-200 rounded-xl border border-stone-200">
              {categories.map(category => {
                const field = RULE_SETTING_FIELDS[category.identifier]
                if (!field) return null
                return (
                  <SettingRow
                    key={category.id}
                    id={`tachograph-rule-${category.identifier}`}
                    className="px-4 py-3"
                    label={`${category.identifier}. ${category.title}`}
                    info={category.description}
                    checked={form[field]}
                    onCheckedChange={checked => update({ [field]: checked })}
                  />
                )
              })}
            </div>
          </fieldset>

          <fieldset className="space-y-2">
            <div className="grid gap-x-6 gap-y-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="tachograph-min-gap" className="leading-snug">
                  {t.minGap}
                </Label>
                <NumberInput
                  id="tachograph-min-gap"
                  min={1}
                  suffix={` ${t.minutes.toLowerCase()}`}
                  value={form.minManualEntryGapMinutes}
                  onChange={value =>
                    update({
                      minManualEntryGapMinutes:
                        value ?? form.minManualEntryGapMinutes,
                    })
                  }
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="tachograph-max-gap" className="leading-snug">
                  {t.maxGap}
                </Label>
                <NumberInput
                  id="tachograph-max-gap"
                  min={1}
                  suffix={` ${t.minutes.toLowerCase()}`}
                  value={form.maxManualEntryGapMinutes}
                  onChange={value =>
                    update({
                      maxManualEntryGapMinutes:
                        value ?? form.maxManualEntryGapMinutes,
                    })
                  }
                />
              </div>
            </div>
            <p className="text-sm text-stone-500">{t.gapInfo}</p>
          </fieldset>

          <div className="flex justify-end border-t border-stone-200 pt-6">
            <Button type="submit" disabled={saving}>
              {t.save}
            </Button>
          </div>
        </form>
      </Section>
    </SettingsWrapper>
  )
}
